import React from 'react';
import { Link } from 'react-router-dom';
import {
  STUDIO_NAME,
  STUDIO_WORDMARK,
  STUDIO_EMAIL,
  STUDIO_INSTAGRAM,
  STUDIO_LOCATION,
  STUDIO_TAGLINE,
} from '@/data/studio';

export default function Footer() {
  return (
    <footer
      className="relative overflow-hidden bg-[#0A1A2F] text-[#F5F3EF] pt-24 pb-12 w-full"
      role="contentinfo"
      itemScope
      itemType="https://schema.org/Organization"
    >
      <picture>
        <source srcSet="/generated/range-travel-real.webp" type="image/webp" />
        <img src="/generated/range-travel-real.jpg" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-[0.13]" width="1122" height="1402" />
      </picture>
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(10,26,47,0.99),rgba(10,26,47,0.88))]" />
      <div className="al-grain absolute inset-0 opacity-[0.05]" aria-hidden />
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Top — wordmark + tagline */}
        <div className="grid gap-12 mb-16 md:grid-cols-2 lg:grid-cols-12">
          <div className="md:col-span-2 lg:col-span-5">
            <div
              className="font-serif text-[44px] sm:text-[62px] leading-[0.92] tracking-[-0.035em] mb-7"
            >
              {STUDIO_WORDMARK.lead}<br />
              <span className="italic text-[#D4AF37]">{STUDIO_WORDMARK.accent}</span>
            </div>
            <p
              className="text-[#F8F4EC]/[0.55] text-[15px] leading-[1.6] max-w-sm"
              itemProp="description"
            >
              {STUDIO_TAGLINE}
            </p>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[11px] tracking-[0.28em] uppercase text-[#C5A059] font-semibold mb-5">
              What we do
            </h4>
            <ul className="space-y-3">
              <li><Link to="/website-redesign" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">Website Redesign</Link></li>
              <li><Link to="/the-new-era-of-search" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">New Era of Search</Link></li>
              <li><Link to="/client-experience" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">Client Experience</Link></li>
              <li><Link to="/work" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">Examples</Link></li>
              <li><Link to="/frequently-asked-questions" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">FAQ</Link></li>
              <li><Link to="/about" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">About</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[11px] tracking-[0.28em] uppercase text-[#C5A059] font-semibold mb-5">
              Reading
            </h4>
            <ul className="space-y-3">
              <li><Link to="/blog/getting-found-in-ai-answers" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">Getting Found in AI Answers</Link></li>
              <li><Link to="/blog/redesigning-for-the-new-search" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">Redesigning For It</Link></li>
              <li><Link to="/blog" className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors">All Insights</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2 lg:col-span-3">
            <h4 className="text-[11px] tracking-[0.28em] uppercase text-[#C5A059] font-semibold mb-5">
              Contact
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${STUDIO_EMAIL}`}
                  className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors"
                  itemProp="email"
                >
                  {STUDIO_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={STUDIO_INSTAGRAM.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F8F4EC]/70 hover:text-[#F8F4EC] text-[14px] transition-colors"
                >
                  {STUDIO_INSTAGRAM.handle}
                </a>
              </li>
              <li className="text-[#F8F4EC]/[0.55] text-[14px]" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <span itemProp="addressLocality">{STUDIO_LOCATION.locality}</span>, <span itemProp="addressCountry">{STUDIO_LOCATION.country}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="h-px bg-[#F8F4EC]/10 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-[#F8F4EC]/40 text-[13px]">
            © 2026 {STUDIO_NAME}. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/privacy-policy" className="text-[#F8F4EC]/40 hover:text-[#F8F4EC]/80 text-[13px]">Privacy</Link>
            <Link to="/terms-of-service" className="text-[#F8F4EC]/40 hover:text-[#F8F4EC]/80 text-[13px]">Terms</Link>
            <Link to="/cookie-policy" className="text-[#F8F4EC]/40 hover:text-[#F8F4EC]/80 text-[13px]">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
